/**
 * Created by denis.gusarov on 3/3/2017.
 */
namespace Factive.Game {

    import List = Factive.Core.Linq.List;
    import Tween = Factive.Core.Tween.Tween;
    import Easing = Factive.Core.Tween.Easing;
    import Texture = PIXI.Texture;
    type PixiArmatureDisplay = dragonBones.PixiArmatureDisplay;
    type EventType = dragonBones.EventObject;
    import _ = Factive.Core.Underscore;
    import DependenciesContainer = Factive.Core.DependenciesContainer;
    import AnimationState = dragonBones.AnimationState;

    export class BaseModel {

    }

    export class TextureItem {
        public name: string;
        public textureName: string;
        public texture: PIXI.Texture;
    }

    export class TextureList {
        public items: List<TextureItem> = new List<TextureItem>();
        public get(name: string): PIXI.Texture {
            return this[name];
        }
    }

    export class SpriteItem {
        public name: string;
        public sprite: PIXI.Sprite;
        public textureName: string;
    }

    export class SpriteList {
        public items: List<SpriteItem> = new List<SpriteItem>();
        public get(name: string): PIXI.Sprite {
            return this[name];
        }
    }

    export class ResourceItem {
        constructor(public name: string,
                    public res: string){

        }
        getResource(): PIXI.loaders.Resource {
            return PIXI.loader.resources[this.name];
        }
        getTexture(): PIXI.Texture {
            return PIXI.Texture.fromFrame(this.name);
        }
    }

    export class ResourcesList {
        public items: List<ResourceItem> = new List<ResourceItem>();
        public get(name: string): ResourceItem {
            return this[name];
        }
    }

    export class SpriteEvents {
        public items: List<SpritesEventItem> = new List<SpritesEventItem>();
    }

    export class SpritesEventItem {
        public name: string;
        public sprite: PIXI.Sprite;
        public event: Function;
    }

    export class ChildItem {
        constructor(public view: ViewBase){
            this.$textures = view.$textures;
            this.$sprites = view.$sprites;
            this.$spritesEvents = view.$spritesEvents;
            this.$childViews = view.$childViews;
            this.$animations = view.$animations;
            this.$animationsEvents = view.$animationsEvents;
        }

        public name: string;
        public viewName: string;

        public $textures: TextureList;
        public $sprites: SpriteList;
        public $spritesEvents: SpriteEvents;
        public $childViews: ChildViews;
        public $animations: AnimationsList;
        public $animationsEvents: AnimationsEventsList;
        public $resources: ResourcesList;
    }

    export class ChildViews {
        public items: List<ChildItem> = new List<ChildItem>();
        public get<T extends ViewBase>(name: string): T {
            if(!this[name]){
                console.error("error can't find child '" + name + "'");
            }
            return this[name].view;
        }
    }

    export class AnimationsItem {
        constructor(private animationName: string,
                    private _armature: dragonBones.Armature){

        }
        public name: string;
        public play(): AnimationState{
            return this._armature.animation.play(this.animationName, 1);
        }
        public gotoAndStopByProgress(index: number): AnimationState{
            return this._armature.animation.gotoAndStopByProgress(this.animationName, index);;
        }
        public checkName(name):boolean{
            return this.animationName == name;
        }
    }

    export class AnimationsList {
        public items: List<AnimationsItem> = new List<AnimationsItem>();
        public get(name: string): AnimationsItem {
            if(!this[name]){
                console.error("error can't find animation '" + name + "'");
            }
            return this[name];
        }
    }

    export class AnimationsEventItem {
        public event: Function;
        public hasCalled: boolean;
        constructor(public eventType: string,
                    private _event: Function,
                    public armature: dragonBones.Armature,
                    public animation: AnimationsItem){
            this.hasCalled = false;
            this.event = (et: EventType) => {
                if(animation.checkName(et.animationState.name)){
                    this.hasCalled = true;
                    _event(et);
                }
            };
            this.armature.addEventListener(eventType, this.event, this);
        }
        public name: string;
    }

    export class AnimationsEventsList {
        public items: List<AnimationsEventItem> = new List<AnimationsEventItem>();
        public get(name: string): AnimationsEventItem {
            return this[name];
        }
    }

    export class ModelWatchItem {
        public event: Function;
        public name: string;
        public context: string;
        constructor(private _event: Function){
            let that = this;
            this.event = function (newVal, oldVal, prop, action) {
                if(prop == that.name && this == that.context){
                    //console.log(that.name, newVal);
                    _event(newVal, oldVal);
                }
            };
        }
    }

    export class ModelWatchesList {
        public items: List<ModelWatchItem> = new List<ModelWatchItem>();
        public get(name: string): ModelWatchItem {
            return this[name];
        }
    }

    export abstract class ViewBase {
        private _dependenciesContainer: DependenciesContainer;
        private _armatureMustDispose: boolean;
        private _isLandscape: boolean;
        private _watchObject: any;
        public get DependenciesContainer(): DependenciesContainer {
            return this._dependenciesContainer;
        }

        private _armature: dragonBones.Armature = null;
        private _armatureDisplay: PixiArmatureDisplay = null;
        private _armatureSlots: List<dragonBones.Slot>;
        private _armatureName: string = null;

        public $textures: TextureList = new TextureList();
        public $sprites: SpriteList = new SpriteList();
        public $spritesEvents: SpriteEvents = new SpriteEvents();
        public $childViews: ChildViews = new ChildViews();
        public $animations: AnimationsList = new AnimationsList();
        public $animationsEvents: AnimationsEventsList = new AnimationsEventsList();
        public $resources: ResourcesList = new ResourcesList();
        public $modelWatches: ModelWatchesList = new ModelWatchesList();

        public abstract $model: BaseModel;
        public config: IViewConfiguration;
        private static _index = 10;

        protected abstract getConfiguration(): IViewConfiguration;
        protected abstract onInitialize(): void;

        public get display(): PIXI.Container {
            return this._armatureDisplay || this._armature.display;
        }

        public get armature(): dragonBones.Armature {
            return this._armature;
        }

        public get z(): number {
            return this._armatureDisplay.zOrder;
        }

        public set z(value: number) {
            this._armatureDisplay.zOrder = value;
        }

        public get x(): number {
            return this._armatureDisplay.x;
        }

        public set x(value: number) {
            this._armatureDisplay.x = value;
        }

        public get y(): number {
            return this._armatureDisplay.y;
        }

        public set y(value: number) {
            this._armatureDisplay.y = value;
        }

        public get armatureDisplay(): PixiArmatureDisplay {
            return this._armatureDisplay;
        }

        public get alpha(): number {
            return this._armatureDisplay.alpha;
        }

        public set alpha(value: number) {
            this._armatureDisplay.alpha = value;
        }

        public constructor(protected container: PIXI.Container = null)
        {

        }

        public configure(){
            this.config = this.getConfiguration();

            // if(this.config.armatureName){
            //     var armature = dragonBones.PixiFactory.factory.buildArmature(this.config.armatureName);
            //     this.initialize(armature, this.container);
            // }
        }

        public build(){
            this.configure();
            this.initialize(this._armature, this.container, true);
            if(this._isLandscape){
                this.setLandscape();
            } else {
                this.setPortrait();
            }
        }

        public initialize(armature: dragonBones.Armature, container = null, build = false) : PIXI.Container {

            let childC = null;

            if(!build){
                if(container){

                    if(armature == null && this.config.armatureName){
                        armature = dragonBones.PixiFactory.factory.buildArmature(this.config.armatureName);
                    }

                    if(armature){
                        this._armatureMustDispose = true;
                        this._armature = armature;
                        this._armatureName = this.config.armatureName;
                        this._armatureDisplay = <PixiArmatureDisplay>this._armature.display;
                        this._armatureSlots = new List<dragonBones.Slot>(this._armature.getSlots());
                        dragonBones.WorldClock.clock.add(this._armature);
                        container.addChild(this._armatureDisplay);
                        this.container = container;

                        let zOrder = 0;
                        if(this.config.zOrder){
                            zOrder = this.config.zOrder;
                        }

                        (<any>this._armature).DisplayGroup = new PIXI.DisplayGroup(zOrder, true);
                    }
                }
                else {
                    if(this.config.slot){
                        let childArmature = armature.getSlot(this.config.slot).childArmature;
                        if(childArmature){
                            if(childArmature.name != this.config.armatureName){
                                console.error("Slot '" + this.config.slot + " have child armature '"+ childArmature.name + "' not '"+ this.config.armatureName + "'");
                                return;
                            }
                            this._armatureMustDispose = true;
                            this._armature = childArmature;
                            this._armatureName = this.config.armatureName;
                            this._armatureDisplay = <PixiArmatureDisplay>this._armature.display;
                            this._armatureSlots = new List<dragonBones.Slot>(this._armature.getSlots());
                        } else{
                            console.error("Can't find child armature '"+ this.config.armatureName + "' in slot '" + this.config.slot + "'");
                            return;
                        }

                    } else if(this.config.armatureName){
                        let childArmature = dragonBones.PixiFactory.factory.buildArmature(this.config.armatureName);
                        if(childArmature){
                            this._armatureMustDispose = true;
                            this._armature = childArmature;
                            this._armatureName = this.config.armatureName;
                            this._armatureDisplay = <PixiArmatureDisplay>this._armature.display;
                            this._armatureSlots = new List<dragonBones.Slot>(this._armature.getSlots());
                            dragonBones.WorldClock.clock.add(this._armature);
                            this.container = <PixiArmatureDisplay>armature.display;
                            this.container.addChild(this._armatureDisplay);

                            childC = childArmature.display;

                            let zOrder = 0;
                            if(this.config.zOrder){
                                zOrder = this.config.zOrder;
                            }

                            (<any>this._armature).DisplayGroup = new PIXI.DisplayGroup(zOrder, true);
                        } else{
                            console.error("Armature '"+this.config.armatureName+"' build error");
                            return;
                        }
                    } else {
                        this._armature = armature;
                    }

                }
            }

            if(!this._armature){
                console.error("View '" + (<any>this.constructor).name + "' can't load");
                return;
            }

            let slots = new List<dragonBones.Slot>(this._armature.getSlots());

            _.each(this.config.textures, function (textureName, key) {
                let textureData = Resources.getTextureData(textureName);
                if(textureData){
                    let textureItem = new TextureItem();
                    textureItem.name = key;
                    textureItem.textureName = textureName;
                    textureItem.texture = textureData.texture;
                    this.$textures.items.Add(textureItem);
                    this.$textures[key] = textureData.texture;
                } else{
                    console.error(this.constructor.name + ": error can't find texture '" + textureName + "'");
                }
            }, this);

            _.each(this.config.sprites, function (spriteName, key) {
                if(spriteName.startsWith("armature(")){
                    let regExp = /\(([^)]+)\)/g;
                    let matches = spriteName.match(regExp);
                    let slotName = matches[0].substring(1, matches[0].length - 1);
                    let slot = <dragonBones.Slot>slots.FirstOrDefault(x => x.name == slotName);
                    if(slot){
                        let spriteItem = new SpriteItem();
                        spriteItem.sprite = slot.display;
                        spriteItem.name = key;
                        spriteItem.textureName = slotName;
                        this.$sprites.items.Add(spriteItem);
                        this.$sprites[key] = slot.display;
                    } else {
                        console.error(this.constructor.name + ": error can't find sprite '" + slotName + "' in armature '" + this._armatureName + "'");
                    }
                } else if(spriteName.startsWith("texture(")){
                    let regExp = /\(([^)]+)\)/g;
                    let matches = spriteName.match(regExp);
                    let textureName = matches[0].substring(1, matches[0].length - 1);
                    let texture = this.$textures[textureName];
                    if(texture){
                        let sprite = new PIXI.Sprite(texture);
                        let spriteItem = <SpriteItem>{
                            sprite: sprite,
                            name: key,
                            textureName: textureName
                        };
                        this.$sprites.items.Add(spriteItem);
                        this.$sprites[key] = sprite;
                    } else {
                        console.error(this.constructor.name + ": error can't find texture '" + textureName + "' in textures list");
                    }
                }
            }, this);

            _.each(this.config.spritesEvents, function (func, key) {

                let eventName = key.split(" ")[0];
                let spriteLink = key.split(" ")[1];
                let sprite:PIXI.Sprite;
                let spriteName = spriteLink.replace("$sprites.","");

                if(spriteLink.startsWith("$sprites."))
                {
                    sprite = this.$sprites[spriteName];
                }
                else {
                    _.each(this, function (sprite, key) {
                        if(key == spriteLink){
                            //TODO
                        }
                    }, this);
                }

                if(sprite){

                    let event = this.expandFunc(func, this);
                    sprite.on(eventName, event, this);

                    sprite.interactive = true;
                    sprite.buttonMode = true;

                    let spritesEventItem = <SpritesEventItem>{
                        event: event,
                        name: eventName,
                        sprite: sprite
                    };

                    this.$spritesEvents.items.Add(spritesEventItem);
                } else {
                    console.error(this.constructor.name + ": error can't find sprite '" + spriteName + "' in sprites list");
                }

            }, this);

            _.each(this.config.childViews, function (child, key) {
                if(child.view){
                    this._addChild(child, key);
                }
            }, this);

            _.each(this.config.resources, function (res, key) {
                let resourceItem = new ResourceItem(key, res);
                this.$resources.items.Add(resourceItem);
                this.$resources[key] = resourceItem;
            }, this);

            _.each(this.config.animations, function (name, key) {
                let animationsItem = new AnimationsItem(name, this._armature);
                this.$animations.items.Add(animationsItem);
                this.$animations[key] = animationsItem;
            }, this);

            _.each(this.config.animationsEvents, function (func, key) {
                let eventType = key.split(" ")[0];
                let animationLink = key.split(" ")[1];

                let animationsItem: AnimationsItem = null;
                if(animationLink.startsWith("$animations."))
                {
                    animationsItem = this.$animations[animationLink.replace("$animations.","")];
                }
                else {
                    _.each(this, function (sprite, key) {
                        if(key == animationLink){
                            //TODO
                        }
                    }, this);
                }

                if(animationsItem){
                    let event = this.expandFunc(func, this);
                    let animationsEventItem = new AnimationsEventItem(eventType, event, this._armature, animationsItem);
                    animationsEventItem.name = key;
                    this.$animationsEvents.items.Add(animationsEventItem);
                    this.$animationsEvents[key] = animationsEventItem;
                }

            }, this);

            this._watchObject = this._dependenciesContainer.get("WatchObject");

            _.each(this.config.modelWatch, function (func, key) {
                if(this.$model == null || this.$model == undefined){
                    console.error(this.constructor.name + ": error $model is null for property '" + key + "'");
                    return;
                }
                let event = this.expandFunc(func, this);
                let modelWatchItem = new ModelWatchItem(event);
                let propPathList = key.split('.');
                modelWatchItem.name = propPathList[propPathList.length - 1];
                modelWatchItem.context = this.$model;
                if(propPathList.length > 1){
                    for (let i = 0; i < propPathList.length - 1; i++){
                        if(!modelWatchItem.context[propPathList[i]]){
                            console.error(this.constructor.name + ": 1. error can't find model property '" + propPathList[i] + "' for '" + key + "'", modelWatchItem.context);
                            return;
                        } else {
                            modelWatchItem.context = modelWatchItem.context[propPathList[i]];
                        }
                    }
                }
                if(modelWatchItem.context[modelWatchItem.name] == null || modelWatchItem.context[modelWatchItem.name] == undefined){
                    console.error(this.constructor.name + ": 2. error can't find model property '" + modelWatchItem.name + "' for '" + key + "'", modelWatchItem.context);
                    return;
                }
                this.$modelWatches.items.Add(modelWatchItem);
                this.$modelWatches[key] = modelWatchItem;
                this._watchObject.watch(this.$model, modelWatchItem.event);
            }, this);

            this.onInitialize();

            if(this.$resources.items.Count() > 0){
                (<any>this._dependenciesContainer.get("FactiveBase")).load(() => {
                    this.contentLoaded();
                }, this);
            }

            return childC;
        }

        public AddChild(child, key = null): ViewBase {
            return this._addChild(child, key);
        }

        public removeChild(child: ViewBase) {
            let childView = this.$childViews.items.FirstOrDefault(v => v.view == child);
            this._removeChild(childView);
        }

        public removeChildByName(childName: string) {
            let childView = this.$childViews.items.FirstOrDefault(v => v.name == childName);
            this._removeChild(childView);
        }

        private _removeChild(childView: ChildItem) {
            if(childView != null){
                this.$childViews.items.Remove(childView);
                childView.view.dispose();
            }
        }

        private _addChild(child, key): ViewBase {
            let view = <ViewBase>this._dependenciesContainer.get(child.view, child.options || {});
            if (view) {
                if (child.model) {
                    view.$model = child.model;
                }
                view.configure();
                Factive.Core.Underscore.extend(view.config, child.config || {});
                let childItem = new ChildItem(view);
                childItem.name = key;
                childItem.viewName = child.view;
                this.$childViews.items.Add(childItem);
                if(key != null){
                    this.$childViews[key] = childItem;
                }
                let childContainer = childItem.view.initialize(this._armature);
                if (childContainer != null) {
                    childContainer.zOrder = child.zOrder;
                    childContainer.displayGroup = (<any>this._armature).DisplayGroup;
                }
                if (child.x != null && child.x != undefined) {
                    view.x = child.x;
                }
                if (child.y != null && child.y != undefined) {
                    view.y = child.y;
                }
            } else {
                console.error((<any>this.constructor).name + ": error can't find view '" + child.view + "'");
            }
            return view;
        }

        public setScale(x:number, y:number){
            this._armatureDisplay.scale.set(x, y);
        }

        protected masker (func, mask, cx, args = null) {
            let isMethod = typeof func == 'string';
            let isSkipFirst = typeof mask == 'number' || mask == null;
            mask == null && (mask = 1);
            args || (args = []);

            if (!isSkipFirst) {
                mask = mask
                    .replace(/\./g, function (ch, i) { return i > 8 ? '-' : i + 1 })
                    .replace(/[^1-9.\-]+/g, '-')
                    .replace(/-+$/, '')
            }

            return function () {
                let callCx = cx || this;
                let callArgs = isSkipFirst ? args.concat(_.rest(arguments, mask)) : args.concat();

                for (let i = 0; i < mask.length; i++) {
                    mask[i] != '-' && callArgs.push(arguments[mask[i] - 1])
                }

                return (isMethod ? callCx[func] : func).apply(callCx, callArgs)
            }
        };

        protected expandFunc (func, obj) {
            if (typeof func == 'string') {
                let parts = func.split(/([.-].*)/);
                if (parts.length > 1) {
                    return this.masker(parts[0], parts[1], obj)
                } else {
                    return function () {
                        let callCx = obj || this;
                        return callCx[func].apply(callCx, arguments)
                    }
                }
            } else {
                return func;//obj ? _.bind(func, obj) : func
            }
        };

        public Update(){
            this.onUpdate();
            this.$childViews.items.ForEach(ch => {
                ch.view.Update();
            });
        }

        protected onUpdate(){

        }

        protected onSetLandscape(){

        }

        public setLandscape(){
            this._isLandscape = true;
            this.onSetLandscape();
            this.$childViews.items.ForEach(ch => {
                ch.view.setLandscape();
            });
        }

        protected onSetPortrait(){

        }

        public getResources(): ResourceItem[] {
            let items = new List<ResourceItem>();
            items.AddRange(this.$resources.items.ToArray());
            this.$childViews.items.ForEach(ch => {
                items.AddRange(ch.view.getResources());
            });
            return items.ToArray();
        }

        public contentLoaded(){
            this.onContentLoaded();
            this.$childViews.items.ForEach(ch => {
                ch.view.contentLoaded();
            });
        }

        protected onContentLoaded(){

        }

        public setPortrait(){
            this._isLandscape = false;
            this.onSetPortrait();
            this.$childViews.items.ForEach(ch => {
                ch.view.setPortrait();
            });
        }


        public dispose() {

            this.$childViews.items.ForEach(ch => {
                ch.view.dispose();
            });

            this.$animationsEvents.items.ForEach(ae => {
                ae.armature.removeEventListener(ae.eventType, ae.event, ae);
            });

            this.$spritesEvents.items.ForEach(se => {
                se.sprite.removeListener(se.name, se.event);
            });

            let that = this;
            this.$modelWatches.items.ForEach(se => {
                this._watchObject.unwatch(that.$model, se.name, se.event);
            });

            if(this.container){
                this.container.removeChild(this._armatureDisplay);
                dragonBones.WorldClock.clock.remove(this._armature);
                this._armature.dispose();
                this._armature = null;
            }
        }

        protected clear(){
            this.$childViews.items.ForEach(ch => {
                ch.view.dispose();
            });
            this.$childViews = new ChildViews();
            this.$textures = new TextureList();
            this.$sprites = new SpriteList();
            this.$spritesEvents = new SpriteEvents();
            this.$animations = new AnimationsList();
            this.$animationsEvents = new AnimationsEventsList();
        }
    }
}


// WEBPACK FOOTER //
// src/typescript/Game/Base/ViewBase.ts